import { api } from "./client";

export interface FlowRow {
  id: string;
  name: string;
  description: string | null;
  status: "draft" | "published" | "archived";
  draft_version_id: string | null;
  published_version_id: string | null;
  published_number: number | null;
  draft_number: number | null;
  active_runs: number;
  updated_at: string;
}
export interface FlowVersion {
  id: string;
  number: number;
  changelog: string | null;
  created_at: string;
  published_at: string | null;
}
export interface FlowTrigger {
  id?: string;
  kind:
    | "channel_default"
    | "keyword"
    | "out_of_hours"
    | "api"
    | "template_reply"
    | "assistant_tool"
    | "tag_added"
    | "manual";
  channel_id?: string | null;
  channelId?: string | null;
  config: Record<string, unknown>;
  is_active?: boolean;
  isActive?: boolean;
}
export interface FlowDetail {
  flow: FlowRow;
  versions: FlowVersion[];
  draft: FlowDefinition | null;
  published: FlowDefinition | null;
  triggers: FlowTrigger[];
}

/** Mirrors apps/api/src/modules/flows/definition.ts; the server validates, the client edits. */
export type FlowNode = Record<string, unknown> & { id: string; kind: string };
export interface FlowDefinition {
  version: 1;
  start: string;
  nodes: FlowNode[];
  exception?: string;
  /** A quiet contact on a menu or a question: after this long the run goes to timeoutNext. */
  inactivitySeconds?: number;
  timeoutNext?: string;
  layout?: Record<string, { x: number; y: number }>;
}

export interface SimulationTranscript {
  steps: Array<{
    kind: "out" | "in" | "action" | "state" | "vars";
    vars?: Record<string, unknown>;
    text?: string;
    action?: { type: string } & Record<string, unknown>;
    state?: {
      status: string;
      nodeId: string | null;
      vars: Record<string, unknown>;
    };
  }>;
  state: {
    status: string;
    nodeId: string | null;
    vars: Record<string, unknown>;
    waitKind?: string;
  };
  outbound: string[];
}

const base = (slug: string) => `/v1/c/${slug}`;

/** A file a flow's message block sends, kept for the flow. */
export interface FlowMediaFile {
  storageKey: string;
  kind: "image" | "video" | "audio" | "document";
  mime: string;
  filename?: string;
  sizeBytes: number;
  width?: number;
  height?: number;
  durationMs?: number;
}
export const flowsApi = {
  list: (slug: string) => api<{ flows: FlowRow[] }>(`${base(slug)}/flows`),
  /** Keeps an upload (POST /uploads) as a file of the flow. */
  keepMedia: (slug: string, uploadId: string) =>
    api<{ media: FlowMediaFile }>(`${base(slug)}/flows/media`, {
      method: "POST",
      body: { uploadId },
    }),
  /** The address of a flow's file, for the builder's preview. */
  mediaUrl: (slug: string, storageKey: string) =>
    `${base(slug)}/flows/media/${storageKey.split("/").pop()}`,
  create: (
    slug: string,
    body: { name: string; description?: string; definition?: FlowDefinition },
  ) =>
    api<{ flow: { id: string } }>(`${base(slug)}/flows`, {
      method: "POST",
      body,
    }),
  get: (slug: string, id: string) =>
    api<FlowDetail>(`${base(slug)}/flows/${id}`),
  rename: (
    slug: string,
    id: string,
    body: { name?: string; description?: string },
  ) => api(`${base(slug)}/flows/${id}`, { method: "PATCH", body }),
  saveDraft: (
    slug: string,
    id: string,
    definition: FlowDefinition,
    changelog?: string,
  ) =>
    api<{ draftVersionId: string }>(`${base(slug)}/flows/${id}/draft`, {
      method: "PUT",
      body: { definition, changelog },
    }),
  publish: (slug: string, id: string, versionNumber?: number) =>
    api<{ number: number }>(`${base(slug)}/flows/${id}/publish`, {
      method: "POST",
      body: versionNumber ? { versionNumber } : {},
    }),
  archive: (slug: string, id: string) =>
    api(`${base(slug)}/flows/${id}/archive`, { method: "POST", body: {} }),
  setTriggers: (
    slug: string,
    id: string,
    triggers: Array<{
      kind: string;
      channelId?: string | null;
      config?: Record<string, unknown>;
      isActive?: boolean;
    }>,
  ) =>
    api(`${base(slug)}/flows/${id}/triggers`, {
      method: "PUT",
      body: triggers,
    }),
  simulate: (
    slug: string,
    id: string,
    body: {
      definition: FlowDefinition;
      inputs: Array<{
        text: string;
        selectedId?: string;
        assistantDone?: boolean;
      }>;
      contact?: { name?: string; fields?: Record<string, unknown> };
      insideBusinessHours?: boolean;
    },
  ) =>
    api<{ transcript: SimulationTranscript }>(
      `${base(slug)}/flows/${id}/simulate`,
      { method: "POST", body },
    ),
  runs: (slug: string, id: string) =>
    api<{ runs: Array<Record<string, unknown>> }>(
      `${base(slug)}/flows/${id}/runs`,
    ),
  channels: (slug: string) =>
    api<{
      channels: Array<{
        id: string;
        display_name: string | null;
        address: string | null;
        default_flow_id: string | null;
      }>;
    }>(`${base(slug)}/channels`),
};
